import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../utils/api.js";
import { useAuthStore } from "../../store/auth.js";
import { STAGE_LABELS, STAGE_COLORS } from "../../utils/stages.js";
import {
  INTERVIEW_SCORE_COLORS,
  INTERVIEW_SCORE_LABELS,
  INTERVIEW_SCORES,
  PANEL_STATUS_COLORS,
  PANEL_STATUS_LABELS,
  type InterviewScore,
  type PanelStatus,
} from "../../utils/interview.js";
import { formatDate } from "../../utils/date.js";

interface CandidateRow {
  id: number;
  jobSlug: string;
  jobTitle: string;
  hiringManager: string | null;
  name: string;
  email: string;
  phone: string | null;
  linkedinUrl: string | null;
  stage: string;
  flagged: boolean;
  hasAssignment: boolean;
  assignmentStatus: string;
  yearsExperience: number | null;
  lastTitle: string | null;
  evalScore: number | null;
  finalInterviewScore: InterviewScore | null;
  panelStatus: PanelStatus | null;
  panelTotal: number;
  panelSubmitted: number;
  latestNote: string | null;
  createdAt: string;
  updatedAt: string;
}

const ALL_STAGES = [
  "applied",
  "assignment",
  "review",
  "interview",
  "interview_complete",
  "offer_pending",
  "offer",
  "talent_pool",
  "rejected",
  "withdrawn",
];

const INTERVIEW_SCORE_RANK: Record<InterviewScore, number> = {
  strong_no_hire: 0,
  no_hire: 1,
  hire: 2,
  strong_hire: 3,
};

const ASSIGNMENT_STATUSES = ["N/A", "Sent", "Submitted", "Overdue"];

type SortField =
  | "name"
  | "jobTitle"
  | "hiringManager"
  | "stage"
  | "assignmentStatus"
  | "evalScore"
  | "finalInterviewScore"
  | "createdAt"
  | "updatedAt";
type SortDir = "asc" | "desc";

const STAGE_ORDER: Record<string, number> = {
  applied: 0,
  assignment: 1,
  review: 2,
  interview: 3,
  interview_complete: 4,
  offer_pending: 5,
  offer: 6,
  talent_pool: 7,
  rejected: 8,
  withdrawn: 9,
};

const ASSIGNMENT_ORDER: Record<string, number> = {
  Overdue: 0,
  Sent: 1,
  Submitted: 2,
  "N/A": 3,
};

function compareCandidates(
  a: CandidateRow,
  b: CandidateRow,
  field: SortField,
  dir: SortDir,
): number {
  let cmp = 0;
  switch (field) {
    case "name":
      cmp = a.name.localeCompare(b.name);
      break;
    case "jobTitle":
      cmp = a.jobTitle.localeCompare(b.jobTitle);
      break;
    case "hiringManager":
      cmp = (a.hiringManager ?? "").localeCompare(b.hiringManager ?? "");
      break;
    case "stage":
      cmp = (STAGE_ORDER[a.stage] ?? 99) - (STAGE_ORDER[b.stage] ?? 99);
      break;
    case "assignmentStatus":
      cmp =
        (ASSIGNMENT_ORDER[a.assignmentStatus] ?? 99) - (ASSIGNMENT_ORDER[b.assignmentStatus] ?? 99);
      break;
    case "evalScore":
      cmp = (a.evalScore ?? -1) - (b.evalScore ?? -1);
      break;
    case "finalInterviewScore":
      cmp =
        (a.finalInterviewScore ? INTERVIEW_SCORE_RANK[a.finalInterviewScore] : -1) -
        (b.finalInterviewScore ? INTERVIEW_SCORE_RANK[b.finalInterviewScore] : -1);
      break;
    case "createdAt":
      cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      break;
    case "updatedAt":
      cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      break;
  }
  return dir === "asc" ? cmp : -cmp;
}

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  applied: ["assignment", "rejected", "talent_pool"],
  assignment: ["review", "rejected", "withdrawn", "talent_pool"],
  review: ["interview", "rejected", "talent_pool"],
  interview: ["offer_pending", "rejected", "talent_pool"],
  interview_complete: ["offer_pending", "rejected", "talent_pool"],
  offer_pending: ["offer", "rejected", "withdrawn", "talent_pool"],
  offer: ["rejected", "withdrawn"],
};

const ASSIGNMENT_COLORS: Record<string, string> = {
  Sent: "bg-yellow-100 text-yellow-800",
  Submitted: "bg-green-100 text-green-800",
  Overdue: "bg-red-100 text-red-800",
  "N/A": "bg-gray-100 text-gray-500",
};

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function matchesSearch(c: CandidateRow, q: string): boolean {
  const lower = q.toLowerCase();
  return (
    c.name.toLowerCase().includes(lower) ||
    c.email.toLowerCase().includes(lower) ||
    c.jobTitle.toLowerCase().includes(lower) ||
    (c.hiringManager?.toLowerCase().includes(lower) ?? false) ||
    (c.latestNote?.toLowerCase().includes(lower) ?? false)
  );
}

function inDateRange(
  createdAt: string,
  mode: { kind: "all" } | { kind: "days"; n: number } | { kind: "custom"; from: Date; to: Date },
): boolean {
  if (mode.kind === "all") return true;
  const t = new Date(createdAt).getTime();
  if (mode.kind === "days") return t >= Date.now() - mode.n * 86400000;
  return t >= mode.from.getTime() && t <= mode.to.getTime();
}

export function CandidateList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const [allCandidates, setAllCandidates] = useState<CandidateRow[]>([]);
  const [rejectionReasons, setRejectionReasons] = useState<{ code: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<
    null | "delete" | "reject" | "assignment" | "interview" | "talent_pool"
  >(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkRef, setBulkRef] = useState("");
  const [bulkRejectionReason, setBulkRejectionReason] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Search with debounce
  const urlQ = searchParams.get("q") ?? "";
  const [localQ, setLocalQ] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ.trim());

  useEffect(() => {
    const t = setTimeout(() => {
      const q = localQ.trim();
      setDebouncedQ(q);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          if (q) n.set("q", q);
          else n.delete("q");
          return n;
        },
        { replace: true },
      );
    }, 200);
    return () => clearTimeout(t);
  }, [localQ, setSearchParams]);

  // Date range
  const [customFrom, setCustomFrom] = useState(searchParams.get("from") ?? "");
  const [customTo, setCustomTo] = useState(searchParams.get("to") ?? "");

  const dateMode = useMemo(() => {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      const fd = new Date(`${from}T00:00:00.000Z`);
      const td = new Date(`${to}T23:59:59.999Z`);
      if (!Number.isNaN(fd.getTime()) && !Number.isNaN(td.getTime())) {
        return { kind: "custom" as const, from: fd, to: td };
      }
    }
    const days = searchParams.get("days");
    if (days) {
      const n = parseInt(days, 10);
      if ([1, 3, 7, 14, 30, 90].includes(n)) return { kind: "days" as const, n };
    }
    return { kind: "all" as const };
  }, [searchParams]);

  const dateSelectValue = useMemo(() => {
    if (searchParams.get("date") === "custom" || searchParams.get("from")) return "custom";
    const d = searchParams.get("days");
    if (d && ["1", "3", "7", "14", "30", "90"].includes(d)) return d;
    return "all";
  }, [searchParams]);

  const updateSp = useCallback(
    (fn: (sp: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          fn(n);
          return n;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setDatePreset = (key: string) => {
    updateSp((n) => {
      n.delete("days");
      n.delete("from");
      n.delete("to");
      n.delete("date");
      if (key === "all") return;
      if (["1", "3", "7", "14", "30", "90"].includes(key)) {
        n.set("days", key);
        return;
      }
      if (key === "custom") {
        n.set("date", "custom");
      }
    });
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    updateSp((n) => {
      n.delete("days");
      n.delete("date");
      n.set("from", customFrom);
      n.set("to", customTo);
    });
  };

  // Simple filters from URL
  const stage = searchParams.get("stage") ?? "";
  const jobSlug = searchParams.get("jobSlug") ?? "";
  const manager = searchParams.get("manager") ?? "";
  const assignment = searchParams.get("assignment") ?? "";
  const flaggedFilter = searchParams.get("flagged") ?? "";
  const evalMin = searchParams.get("evalMin") ?? "";
  const evalMax = searchParams.get("evalMax") ?? "";
  const interviewScoreFilter = searchParams.get("interviewScore") ?? "";

  const idsParam = searchParams.get("ids") ?? "";
  const urlNote = searchParams.get("note") ?? "";
  const urlRef = searchParams.get("ref") ?? "";
  const idsFilter = useMemo(() => {
    if (!idsParam) return null;
    const parsed = idsParam
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    return parsed.length > 0 ? new Set(parsed) : null;
  }, [idsParam]);

  const updateFilter = (key: string, value: string) => {
    updateSp((n) => {
      if (value) n.set(key, value);
      else n.delete(key);
    });
  };

  // Fetch all candidates (no server-side filtering)
  const fetchCandidates = useCallback(() => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    api
      .get<{ results: CandidateRow[] }>("/candidates")
      .then((d) => setAllCandidates(d.results))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load candidates"))
      .finally(() => setLoading(false));
    api
      .get<{ code: string; label: string }[]>("/candidates/rejection-reasons")
      .then(setRejectionReasons)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Derived data for dropdowns
  const uniqueJobs = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of allCandidates) map.set(c.jobSlug, c.jobTitle);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allCandidates]);

  const uniqueManagers = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCandidates) if (c.hiringManager) set.add(c.hiringManager);
    return Array.from(set).sort();
  }, [allCandidates]);

  // Client-side filtering
  const filtered = useMemo(() => {
    return allCandidates.filter((c) => {
      if (idsFilter && !idsFilter.has(c.id)) return false;
      if (debouncedQ && !matchesSearch(c, debouncedQ)) return false;
      if (stage && c.stage !== stage) return false;
      if (jobSlug && c.jobSlug !== jobSlug) return false;
      if (manager && c.hiringManager !== manager) return false;
      if (assignment && c.assignmentStatus !== assignment) return false;
      if (flaggedFilter === "yes" && !c.flagged) return false;
      if (flaggedFilter === "no" && c.flagged) return false;
      if (evalMin) {
        const min = parseInt(evalMin, 10);
        if (!Number.isNaN(min) && (c.evalScore == null || c.evalScore < min)) return false;
      }
      if (evalMax) {
        const max = parseInt(evalMax, 10);
        if (!Number.isNaN(max) && (c.evalScore == null || c.evalScore > max)) return false;
      }
      if (interviewScoreFilter) {
        if (interviewScoreFilter === "none" && c.finalInterviewScore) return false;
        if (interviewScoreFilter !== "none" && c.finalInterviewScore !== interviewScoreFilter)
          return false;
      }
      if (!inDateRange(c.createdAt, dateMode)) return false;
      return true;
    });
  }, [
    allCandidates,
    idsFilter,
    debouncedQ,
    stage,
    jobSlug,
    manager,
    assignment,
    flaggedFilter,
    evalMin,
    evalMax,
    interviewScoreFilter,
    dateMode,
  ]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => compareCandidates(a, b, sortField, sortDir)),
    [filtered, sortField, sortDir],
  );

  const toggleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir(field === "createdAt" || field === "updatedAt" ? "desc" : "asc");
      }
    },
    [sortField],
  );

  const hasActiveFilters =
    !!idsFilter ||
    !!debouncedQ ||
    !!stage ||
    !!jobSlug ||
    !!manager ||
    !!assignment ||
    !!flaggedFilter ||
    !!evalMin ||
    !!evalMax ||
    !!interviewScoreFilter ||
    dateMode.kind !== "all";

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map((c) => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkRunning(true);
    try {
      await api.delete("/candidates", { ids: Array.from(selected) });
      setSelected(new Set());
      closeBulkModal();
      fetchCandidates();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
      closeBulkModal();
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkStage = async (stage: string) => {
    setBulkRunning(true);
    try {
      const res = await api.post<{ succeeded: number; failed: number }>("/candidates/bulk-stage", {
        ids: Array.from(selected),
        stage,
        note: bulkNote.trim() || undefined,
        ref: bulkRef.trim() || undefined,
        rejectionReason: stage === "rejected" ? bulkRejectionReason || undefined : undefined,
      });
      setSelected(new Set());
      closeBulkModal();
      fetchCandidates();
      if (res.failed > 0) {
        setBulkResult(`${res.succeeded} updated, ${res.failed} skipped (ineligible)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk action failed");
      closeBulkModal();
    } finally {
      setBulkRunning(false);
    }
  };

  const closeBulkModal = () => {
    setBulkAction(null);
    setBulkNote("");
    setBulkRef("");
    setBulkRejectionReason("");
  };

  const selectedCandidates = useMemo(
    () => sorted.filter((c) => selected.has(c.id)),
    [sorted, selected],
  );

  const canBulkReject = selectedCandidates.some((c) => {
    const allowed = ALLOWED_TRANSITIONS[c.stage];
    return allowed?.includes("rejected");
  });

  const canBulkTalentPool = selectedCandidates.some((c) => {
    const allowed = ALLOWED_TRANSITIONS[c.stage];
    return allowed?.includes("talent_pool");
  });

  const canBulkAssignment = selectedCandidates.some((c) => {
    const allowed = ALLOWED_TRANSITIONS[c.stage];
    return c.hasAssignment && allowed?.includes("assignment");
  });

  const canBulkInterview = selectedCandidates.some(
    (c) => !c.hasAssignment && c.stage === "applied",
  );

  const clearAll = () => {
    setLocalQ("");
    setDebouncedQ("");
    setBulkNote("");
    setBulkRef("");
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Candidates</h1>
          <p className="text-sm text-gray-500 mt-1">
            {hasActiveFilters
              ? `Showing ${filtered.length} of ${allCandidates.length} candidates`
              : `${allCandidates.length} candidate${allCandidates.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              {canBulkAssignment && (
                <button
                  onClick={() => {
                    setBulkNote(urlNote);
                    setBulkRef(urlRef);
                    setBulkAction("assignment");
                  }}
                  className="rounded-md bg-yellow-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-yellow-700 transition-colors"
                >
                  Send Assignment ({selected.size})
                </button>
              )}
              {canBulkInterview && (
                <button
                  onClick={() => {
                    setBulkNote(urlNote);
                    setBulkRef(urlRef);
                    setBulkAction("interview");
                  }}
                  className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
                >
                  Invite to Interview ({selected.size})
                </button>
              )}
              {canBulkTalentPool && (
                <button
                  onClick={() => {
                    setBulkNote(urlNote);
                    setBulkRef(urlRef);
                    setBulkAction("talent_pool");
                  }}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                >
                  Talent Pool ({selected.size})
                </button>
              )}
              {canBulkReject && (
                <button
                  onClick={() => {
                    setBulkNote(urlNote);
                    setBulkRef(urlRef);
                    setBulkAction("reject");
                  }}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                >
                  Reject ({selected.size})
                </button>
              )}
              {user?.isAdmin && (
                <button
                  onClick={() => setBulkAction("delete")}
                  className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  Delete ({selected.size})
                </button>
              )}
            </div>
          )}
          <Link to="/dashboard" className="text-sm text-primary-700 hover:text-primary-800">
            &larr; Dashboard
          </Link>
        </div>
      </div>

      {/* IDs filter banner */}
      {idsFilter && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-800">
              Filtered to {idsFilter.size} specific candidate ID{idsFilter.size !== 1 ? "s" : ""}:{" "}
              {Array.from(idsFilter).join(", ")}
            </p>
            <button
              onClick={() =>
                updateSp((n) => {
                  n.delete("ids");
                  n.delete("note");
                  n.delete("ref");
                })
              }
              className="text-sm text-primary-700 hover:text-primary-800 font-medium"
            >
              Clear
            </button>
          </div>
          {(urlNote || urlRef) && (
            <div className="text-xs text-blue-700 space-y-0.5">
              {urlNote && (
                <p>
                  <span className="font-medium">Note:</span> {urlNote}
                </p>
              )}
              {urlRef && (
                <p>
                  <span className="font-medium">Ref:</span>{" "}
                  {isSafeUrl(urlRef) ? (
                    <a
                      href={urlRef}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-primary-900"
                    >
                      {urlRef.length > 80 ? urlRef.slice(0, 80) + "…" : urlRef}
                    </a>
                  ) : (
                    <span>{urlRef.length > 80 ? urlRef.slice(0, 80) + "…" : urlRef}</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters toolbar */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-0.5 text-xs text-gray-600 min-w-[160px]">
            <span className="font-medium text-gray-500">Search</span>
            <div className="relative">
              <input
                type="text"
                value={localQ}
                onChange={(e) => setLocalQ(e.target.value)}
                placeholder="Name, email, position..."
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm pr-7"
              />
              {localQ && (
                <button
                  onClick={() => {
                    setLocalQ("");
                    setDebouncedQ("");
                    updateSp((n) => n.delete("q"));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Stage</span>
            <select
              value={stage}
              onChange={(e) => updateFilter("stage", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All stages</option>
              {ALL_STAGES.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Position</span>
            <select
              value={jobSlug}
              onChange={(e) => updateFilter("jobSlug", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All positions</option>
              {uniqueJobs.map(([slug, title]) => (
                <option key={slug} value={slug}>
                  {title}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Hiring Manager</span>
            <select
              value={manager}
              onChange={(e) => updateFilter("manager", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All managers</option>
              {uniqueManagers.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Assignment</span>
            <select
              value={assignment}
              onChange={(e) => updateFilter("assignment", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {ASSIGNMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Flagged</span>
            <select
              value={flaggedFilter}
              onChange={(e) => updateFilter("flagged", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="yes">Flagged</option>
              <option value="no">Not flagged</option>
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Eval Score</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={10}
                value={evalMin}
                onChange={(e) => updateFilter("evalMin", e.target.value)}
                placeholder="Min"
                className="w-14 rounded border border-gray-300 px-1.5 py-1.5 text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="number"
                min={0}
                max={10}
                value={evalMax}
                onChange={(e) => updateFilter("evalMax", e.target.value)}
                placeholder="Max"
                className="w-14 rounded border border-gray-300 px-1.5 py-1.5 text-sm"
              />
            </div>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Interview Score</span>
            <select
              value={interviewScoreFilter}
              onChange={(e) => updateFilter("interviewScore", e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">Any</option>
              {INTERVIEW_SCORES.map((s) => (
                <option key={s} value={s}>
                  {INTERVIEW_SCORE_LABELS[s]}
                </option>
              ))}
              <option value="none">Not scored</option>
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Date</span>
            <select
              value={dateSelectValue}
              onChange={(e) => setDatePreset(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="all">All time</option>
              <option value="1">Last 24 hours</option>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom...</option>
            </select>
          </label>

          {dateSelectValue === "custom" && (
            <>
              <label className="flex flex-col gap-0.5 text-xs text-gray-600">
                <span className="font-medium text-gray-500">From</span>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="flex flex-col gap-0.5 text-xs text-gray-600">
                <span className="font-medium text-gray-500">To</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <button
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
                className="rounded bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                Apply
              </button>
            </>
          )}

          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Bulk result toast */}
      {bulkResult && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-center justify-between">
          <span>{bulkResult}</span>
          <button
            onClick={() => setBulkResult(null)}
            className="text-yellow-600 hover:text-yellow-800 ml-3"
          >
            &times;
          </button>
        </div>
      )}

      {/* Bulk action confirmation modal */}
      {bulkAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            {bulkAction === "delete" && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Delete candidates</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Permanently delete <span className="font-medium">{selected.size}</span> candidate
                  {selected.size !== 1 ? "s" : ""}? This cannot be undone. All notes, history, and
                  application data will be removed.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={closeBulkModal}
                    disabled={bulkRunning}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkRunning}
                    className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkRunning ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </>
            )}

            {bulkAction === "reject" && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Reject candidates</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Reject <span className="font-medium">{selected.size}</span> selected candidate
                  {selected.size !== 1 ? "s" : ""}? Candidates not in a rejectable stage will be
                  skipped. A rejection email will be sent to each.
                </p>
                <select
                  value={bulkRejectionReason}
                  onChange={(e) => setBulkRejectionReason(e.target.value)}
                  className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500"
                >
                  <option value="">Select rejection reason...</option>
                  {rejectionReasons.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <textarea
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Additional note (optional)..."
                  rows={2}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <input
                  type="url"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Reference URL (optional, e.g. Slack thread)..."
                  value={bulkRef}
                  onChange={(e) => setBulkRef(e.target.value)}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={closeBulkModal}
                    disabled={bulkRunning}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleBulkStage("rejected")}
                    disabled={bulkRunning || !bulkRejectionReason}
                    className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkRunning ? "Rejecting..." : "Reject"}
                  </button>
                </div>
              </>
            )}

            {bulkAction === "talent_pool" && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Move to talent pool</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Move <span className="font-medium">{selected.size}</span> selected candidate
                  {selected.size !== 1 ? "s" : ""} to the talent pool? Candidates not in an eligible
                  stage will be skipped. A keep-in-touch email will be sent to each.
                </p>
                <textarea
                  className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="What future role or strengths should we remember? (required)"
                  rows={2}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <input
                  type="url"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Reference URL (optional, e.g. Slack thread)..."
                  value={bulkRef}
                  onChange={(e) => setBulkRef(e.target.value)}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={closeBulkModal}
                    disabled={bulkRunning}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleBulkStage("talent_pool")}
                    disabled={bulkRunning || !bulkNote.trim()}
                    className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkRunning ? "Moving..." : "Move to Talent Pool"}
                  </button>
                </div>
              </>
            )}

            {bulkAction === "assignment" && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Send assignment</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Send the assignment to <span className="font-medium">{selected.size}</span>{" "}
                  selected candidate{selected.size !== 1 ? "s" : ""}? Candidates not in the
                  &ldquo;Applied&rdquo; stage will be skipped. An assignment email will be sent to
                  each.
                </p>
                <textarea
                  className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Optional note..."
                  rows={2}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <input
                  type="url"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Reference URL (optional, e.g. Slack thread)..."
                  value={bulkRef}
                  onChange={(e) => setBulkRef(e.target.value)}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={closeBulkModal}
                    disabled={bulkRunning}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleBulkStage("assignment")}
                    disabled={bulkRunning}
                    className="rounded-md bg-yellow-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkRunning ? "Sending..." : "Send Assignment"}
                  </button>
                </div>
              </>
            )}

            {bulkAction === "interview" && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Invite to interview</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Invite <span className="font-medium">{selected.size}</span> selected candidate
                  {selected.size !== 1 ? "s" : ""} to interview? Candidates with assignments or
                  candidates not in the &ldquo;Applied&rdquo; stage will be skipped. An interview
                  invitation email will be sent to each eligible candidate.
                </p>
                <textarea
                  className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Optional note..."
                  rows={2}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <input
                  type="url"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Reference URL (optional, e.g. Slack thread)..."
                  value={bulkRef}
                  onChange={(e) => setBulkRef(e.target.value)}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={closeBulkModal}
                    disabled={bulkRunning}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleBulkStage("interview")}
                    disabled={bulkRunning}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkRunning ? "Inviting..." : "Invite to Interview"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {error ? (
        <div className="text-red-600">Error: {error}</div>
      ) : loading ? (
        <div className="text-gray-400 animate-pulse">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
          {hasActiveFilters ? "No candidates match your filters." : "No candidates found."}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === sorted.length && sorted.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                  />
                </th>
                <SortHeader
                  field="name"
                  label="Name"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortHeader
                  field="jobTitle"
                  label="Position"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortHeader
                  field="hiringManager"
                  label="Hiring Manager"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortHeader
                  field="stage"
                  label="Stage"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortHeader
                  field="assignmentStatus"
                  label="Assignment"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <th className="px-4 py-3">Panel</th>
                <SortHeader
                  field="finalInterviewScore"
                  label="Final Score"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <th className="px-4 py-3">Experience</th>
                <th className="px-4 py-3">Last Title</th>
                <SortHeader
                  field="evalScore"
                  label="Eval"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <th className="px-4 py-3">Latest Note</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3 w-8">LI</th>
                <SortHeader
                  field="createdAt"
                  label="Applied"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortHeader
                  field="updatedAt"
                  label="Updated"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((c) => (
                <tr
                  key={c.id}
                  className={`hover:bg-gray-50 transition-colors ${selected.has(c.id) ? "bg-blue-50" : ""}`}
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      className="rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/candidates/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary-700 hover:text-primary-900"
                      >
                        {c.name}
                      </Link>
                      {c.flagged && (
                        <span className="text-red-500 text-sm" title="Flagged">
                          &#9873;
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{c.email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.jobTitle}</td>
                  <td className="px-4 py-3 text-gray-600">{c.hiringManager ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STAGE_COLORS[c.stage] ?? "bg-gray-100 text-gray-600"}`}
                    >
                      {STAGE_LABELS[c.stage] ?? c.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${ASSIGNMENT_COLORS[c.assignmentStatus] ?? "bg-gray-100 text-gray-500"}`}
                    >
                      {c.assignmentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.panelStatus && c.panelStatus !== "no_panel" ? (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${PANEL_STATUS_COLORS[c.panelStatus]}`}
                        title={
                          c.panelStatus === "collecting"
                            ? `${c.panelSubmitted} of ${c.panelTotal} submitted`
                            : PANEL_STATUS_LABELS[c.panelStatus]
                        }
                      >
                        {PANEL_STATUS_LABELS[c.panelStatus]}
                        {c.panelStatus === "collecting" && c.panelTotal > 0 && (
                          <span className="opacity-70">
                            {c.panelSubmitted}/{c.panelTotal}
                          </span>
                        )}
                      </span>
                    ) : c.panelStatus === "no_panel" && c.stage === "interview" ? (
                      <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                        Panel Pending
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.finalInterviewScore ? (
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${INTERVIEW_SCORE_COLORS[c.finalInterviewScore]}`}
                      >
                        {INTERVIEW_SCORE_LABELS[c.finalInterviewScore]}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {c.yearsExperience != null ? `${c.yearsExperience}y` : "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-gray-600 text-xs max-w-[150px] truncate"
                    title={c.lastTitle ?? undefined}
                  >
                    {c.lastTitle ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-xs font-medium">
                    {c.evalScore != null ? (
                      <span
                        className={
                          c.evalScore >= 8
                            ? "text-green-700"
                            : c.evalScore >= 5
                              ? "text-yellow-700"
                              : "text-red-600"
                        }
                      >
                        {c.evalScore}/10
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td
                    className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate"
                    title={c.latestNote ?? undefined}
                  >
                    {c.latestNote
                      ? c.latestNote.length > 60
                        ? c.latestNote.slice(0, 60) + "…"
                        : c.latestNote
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{c.phone ?? "—"}</td>
                  <td className="px-4 py-3">
                    {c.linkedinUrl && isSafeUrl(c.linkedinUrl) ? (
                      <a
                        href={c.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="LinkedIn"
                        className="text-primary-700 hover:text-primary-800"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </a>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SortHeader({
  field,
  label,
  sortField,
  sortDir,
  onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th className="px-4 py-3">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700 transition-colors"
      >
        {label}
        <span
          className={`inline-flex flex-col text-[8px] leading-none ${active ? "text-gray-700" : "text-gray-300"}`}
        >
          <span className={active && sortDir === "asc" ? "text-gray-700" : "text-gray-300"}>
            &#9650;
          </span>
          <span className={active && sortDir === "desc" ? "text-gray-700" : "text-gray-300"}>
            &#9660;
          </span>
        </span>
      </button>
    </th>
  );
}
